import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { maybeHandleRevokedSession } from "@/lib/sessionExpired";
import { diagnosticErrorDetails, logFactoryAuthDiagnostic } from "@/lib/factoryAuthDiagnostics";

import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

function LegacyTasksRedirect() {
  const loc = useLocation();
  return <Navigate to={{ pathname: '/tasks-list', search: loc.search, hash: loc.hash }} replace />;
}
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { RouteLoadingIndicator } from "@/components/RouteLoadingIndicator";
import { GlobalLoadingListener } from "@/components/GlobalLoadingListener";
import { useLoadingStore } from "@/stores/loadingStore";
import { retryImport } from "@/utils/retryImport";

// Eager-loaded pages (public, auth)
import Landing from "./pages/Landing";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Verify from "./pages/Verify";
import AcceptInvite from "./pages/AcceptInvite";
import SetupPassword from "./pages/SetupPassword";
import ChangePassword from "./pages/ChangePassword";
import ConsumerPortal from "./pages/ConsumerPortal";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages with retry mechanism for resilient module loading
const Dashboard = lazy(() => retryImport(() => import("./pages/Dashboard")));

const OrderManagement = lazy(() => retryImport(() => import("./pages/orders/OrderManagement")));
const POSSync = lazy(() => retryImport(() => import("./pages/orders/POSSync")));
const Drivers = lazy(() => retryImport(() => import("./pages/Drivers")));
const FleetVehicles = lazy(() => retryImport(() => import("./pages/FleetVehicles")));
const LiveMapPage = lazy(() => retryImport(() => import("./pages/LiveMap")));
const Operations = lazy(() => retryImport(() => import("./pages/Operations")));
const Driver = lazy(() => retryImport(() => import("./pages/Driver")));
// RM-6 pilot: isolated guest-driver entry. Deliberately outside ProtectedRoute /
// MainLayout / ModuleAccessGuard — it carries no Supabase user identity and its
// own edge function is the sole authority for token+PIN and shift scope.
const GuestDriverAccess = lazy(() => retryImport(() => import("./pages/GuestDriverAccess")));
const Reports = lazy(() => retryImport(() => import("./pages/Reports")));
const SuperAdmin = lazy(() => retryImport(() => import("./pages/SuperAdmin")));
const Contacts = lazy(() => retryImport(() => import("./pages/Contacts")));
const Locations = lazy(() => retryImport(() => import("./pages/Locations")));
const TrackAndTrace = lazy(() => retryImport(() => import("./pages/TrackAndTrace")));
const RoleManagement = lazy(() => retryImport(() => import("./pages/admin/RoleManagement")));
const TeamManagement = lazy(() => retryImport(() => import("./pages/admin/TeamManagement")));
const UserManagement = lazy(() => retryImport(() => import("./pages/admin/UserManagement")));
const OutletManagement = lazy(() => retryImport(() => import("./pages/admin/OutletManagement")));
const FactoryManagement = lazy(() => retryImport(() => import("./pages/admin/FactoryManagement")));
const FactoryStaffManagement = lazy(() => retryImport(() => import("./pages/admin/FactoryStaffManagement")));
const OutletStaffManagement = lazy(() => retryImport(() => import("./pages/admin/OutletStaffManagement")));
const SecurityEvents = lazy(() => retryImport(() => import("./pages/admin/SecurityEvents")));
const PartnershipManagement = lazy(() => retryImport(() => import("./pages/admin/PartnershipManagement")));
const VendorAssignments = lazy(() => retryImport(() => import("./pages/admin/VendorAssignments")));
// Phase 4 debug surface — not added to navigation. Remove at end of Phase 5.
const StatusDriftReport = lazy(() => retryImport(() => import("./pages/admin/StatusDriftReport")));
const SystemRepairTaskWindowMirror = lazy(() => retryImport(() => import("./pages/admin/SystemRepairTaskWindowMirror")));
const Monitoring = lazy(() => retryImport(() => import("./pages/Monitoring")));
const DataHealth = lazy(() => retryImport(() => import("./pages/DataHealth")));
const OutletPortal = lazy(() => retryImport(() => import("./pages/OutletPortal")));
const OutletAuth = lazy(() => retryImport(() => import("./pages/OutletAuth")));
const OutletUserAuth = lazy(() => retryImport(() => import("./pages/OutletUserAuth")));
const Approvals = lazy(() => retryImport(() => import("./pages/Approvals")));
const RouteManagement = lazy(() => retryImport(() => import("./pages/RouteManagement")));
// RM V3 · RM-1 — read-only Planning Board (preview), separately routed and
// lazy-loaded so /route-management never pays for it.
const PlanningBoardPage = lazy(() => retryImport(() => import("./pages/PlanningBoard")));
const LogisticsRequests = lazy(() => retryImport(() => import("./pages/LogisticsRequests")));
const TasksList = lazy(() => retryImport(() => import("./pages/TasksList")));
const FactoryAuth = lazy(() => retryImport(() => import("./pages/FactoryAuth")));
const FactoryUserAuth = lazy(() => retryImport(() => import("./pages/FactoryUserAuth")));
const FactoryPortal = lazy(() => retryImport(() => import("./pages/FactoryPortal")));
const Account = lazy(() => retryImport(() => import("./pages/Account")));
const SuperAdminLogin = lazy(() => retryImport(() => import("./pages/SuperAdminLogin")));

import ProtectedRoute from "./components/ProtectedRoute";
import { ModuleAccessGuard } from "./components/ModuleAccessGuard";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OutletLoginProvider } from "./contexts/OutletLoginContext";
import { FactoryLoginProvider } from "./contexts/FactoryLoginContext";

// Singleton QueryClient that persists across hot reloads
let queryClient: QueryClient;
function getQueryClient() {
  if (!queryClient) {
    // A request-level 401 can be transient or endpoint-specific. Confirm the
    // Supabase session with a refresh before clearing it; 403/RLS denials are
    // never treated as session loss.
    const recoveredQueries = new WeakSet<object>();
    const onQuerySessionError = (error: unknown, query: { queryKey: readonly unknown[] }) => {
      void logFactoryAuthDiagnostic('query_auth_error', diagnosticErrorDetails(error));
      void maybeHandleRevokedSession(
        error,
        () => supabaseClient.auth.refreshSession(),
        () => supabaseClient.auth.signOut({ scope: 'local' })
      ).then((outcome) => {
        // Retry read queries once with the refreshed token. Never replay a
        // mutation automatically because it may have partially succeeded.
        if (outcome === 'refreshed' && !recoveredQueries.has(query)) {
          recoveredQueries.add(query);
          void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
        }
      });
    };
    const onMutationSessionError = (error: unknown) => {
      void logFactoryAuthDiagnostic('query_auth_error', diagnosticErrorDetails(error));
      void maybeHandleRevokedSession(
        error,
        () => supabaseClient.auth.refreshSession(),
        () => supabaseClient.auth.signOut({ scope: 'local' })
      );
    };

    queryClient = new QueryClient({
      queryCache: new QueryCache({ onError: onQuerySessionError }),
      mutationCache: new MutationCache({ onError: onMutationSessionError }),

      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 3, // 3 minutes default
          gcTime: 1000 * 60 * 10, // 10 minutes garbage collection time
          // Focus/reconnect refetching is OFF globally — tab-switching should not cause
          // every page to re-fetch and flash spinners. Driver views re-enable this via
          // useDriverTabFocusRefetch (see src/pages/Driver.tsx) with an explicit,
          // enumerated set of driver query keys.
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchOnMount: true,
          retry: 1, // Reduce retries for faster failure detection
        },
      },
    });
  }
  return queryClient;
}

// Handle Supabase auth params present in URL on any route (root, auth, etc.)
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBuildVersionRefresh } from '@/hooks/useBuildVersionRefresh';

const AuthUrlHandler = () => {
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(
      window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
    );
    const getParam = (k: string) => query.get(k) ?? hash.get(k);

    const code = getParam('code');
    const access_token = getParam('access_token');
    const refresh_token = getParam('refresh_token');
    const token_hash = getParam('token_hash');
    const type = getParam('type');

    // Avoid duplicate handling on dedicated auth pages
    if (window.location.pathname === '/change-password' || window.location.pathname === '/verify' || window.location.pathname === '/accept-invite') return;

    const clearUrl = () => window.history.replaceState({}, document.title, window.location.pathname);

    if (code) {
      supabase.auth.exchangeCodeForSession(code).finally(clearUrl);
    } else if (access_token && refresh_token) {
      supabase.auth.setSession({ access_token, refresh_token }).finally(clearUrl);
    } else if (token_hash && type) {
      supabase.auth.verifyOtp({ token_hash, type: type as any }).finally(clearUrl);
    }
  }, []);
  return null;
};

const MainLayout = ({ children }: { children: ReactNode }) => (
  <SidebarProvider defaultOpen={false}>
    <div className="min-h-screen flex w-full">
      <AppSidebar />
      <div className="flex-1 flex flex-col">
        <header className="h-12 flex items-center border-b bg-card px-4 print:hidden">
          <SidebarTrigger />
        </header>
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  </SidebarProvider>
);

// Wrapper for protected routes that includes Auth and Tenant contexts
const ProtectedRoutesWrapper = ({ children }: { children: ReactNode }) => (
  <AuthProvider>
    <TenantProvider>
      {children}
    </TenantProvider>
  </AuthProvider>
);

// Component to handle loading indicator - must be inside providers
const LoadingIndicator = () => {
  const { isRouteLoading } = useLoadingStore();
  // Long-lived tablets pick up new deploys without a forced logout.
  useBuildVersionRefresh();
  
  
  return (
    <>
      {isRouteLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}
      <GlobalLoadingListener />
    </>
  );
};

const App = () => {
  const client = getQueryClient();
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <OutletLoginProvider>
          <FactoryLoginProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AuthUrlHandler />
                <RouteLoadingIndicator>
                  <LoadingIndicator />
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                  <Routes>
                  {/* Public routes - NO Auth/Tenant context */}
                  <Route path="/" element={<Landing />} />
          <Route 
            path="/auth" 
            element={
              <ErrorBoundary>
                <Auth />
              </ErrorBoundary>
            } 
          />
                  <Route path="/outlet-auth" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><OutletAuth /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/outlet-user-auth" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><OutletUserAuth /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/outlet-portal" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><OutletPortal /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/factory-auth" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><FactoryAuth /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/factory-user-auth" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><FactoryUserAuth /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/factory-portal" element={
                    <AuthProvider>
                      <TenantProvider>
                        <ModuleAccessGuard><FactoryPortal /></ModuleAccessGuard>
                      </TenantProvider>
                    </AuthProvider>
                  } />
                  <Route path="/verify" element={<Verify />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/setup-password" element={
                    <AuthProvider>
                      <SetupPassword />
                    </AuthProvider>
                  } />
                  <Route path="/change-password" element={<ChangePassword />} />
                  <Route path="/super-admin/login" element={<SuperAdminLogin />} />
                  <Route path="/book/:tenantSlug" element={<ConsumerPortal />} />
                  <Route path="/track/:trackingId" element={<PublicTrackingPage />} />
                  <Route path="*" element={<NotFound />} />
                  
                  {/* Protected routes - wrapped with Auth/Tenant context */}
                  <Route path="/dashboard" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/account" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Account /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/tasks" element={<LegacyTasksRedirect />} />
                  <Route path="/tasks/*" element={<LegacyTasksRedirect />} />
                  <Route path="/orders/pos-sync" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><POSSync /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/orders/order-management" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><OrderManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/contacts" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Contacts /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/locations" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Locations /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/drivers-management" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><Drivers /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/drivers" element={<Navigate to="/drivers-management" replace />} />
                  <Route path="/map" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><LiveMapPage /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/fleet/vehicles" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><FleetVehicles /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/route-management" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><RouteManagement /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/route-management/planning-board" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><PlanningBoardPage /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/track" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><TrackAndTrace /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/guest-driver" element={<GuestDriverAccess />} />
                  <Route path="/reports" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Reports /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/roles" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><RoleManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/teams" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><TeamManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/users" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ErrorBoundary><UserManagement /></ErrorBoundary></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/outlets" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><OutletManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/factory-management/listing" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><FactoryManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/factory-management/staff" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><FactoryStaffManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/outlet-staff" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><OutletStaffManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/security-events" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><SecurityEvents /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/partnerships" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><PartnershipManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/vendor-assignments" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><VendorAssignments /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />

                  {/* Phase 4 debug-only; not linked from navigation. */}
                  <Route path="/admin/status-drift" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><StatusDriftReport /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/system-repairs/task-window-mirror" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><SystemRepairTaskWindowMirror /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/approvals" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Approvals /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/logistics-requests" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><LogisticsRequests /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/logistics-request" element={<Navigate to="/logistics-requests" replace />} />
                  <Route path="/tasks-list" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><TasksList /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/admin/settings" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><div className="p-8"><h1>System Settings - Coming Soon</h1></div></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/monitoring" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Monitoring /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/data-health" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><DataHealth /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  
                  
                  {/* Legacy /app/* routes */}
                  <Route path="/app/dashboard" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/app/tasks" element={<LegacyTasksRedirect />} />
                  <Route path="/app/tasks/*" element={<LegacyTasksRedirect />} />
                  <Route path="/app/orders/order-management" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><OrderManagement /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/app/drivers" element={<Navigate to="/drivers-management" replace />} />
                  <Route path="/app/map" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><ModuleAccessGuard><LiveMapPage /></ModuleAccessGuard></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/app/reports" element={<ProtectedRoutesWrapper><ProtectedRoute><MainLayout><Reports /></MainLayout></ProtectedRoute></ProtectedRoutesWrapper>} />
                  
                  {/* Legacy routes */}
                  <Route path="/operations" element={<ProtectedRoutesWrapper><ProtectedRoute><ModuleAccessGuard><Operations /></ModuleAccessGuard></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/driver/*" element={<ProtectedRoutesWrapper><ProtectedRoute><ModuleAccessGuard><Driver /></ModuleAccessGuard></ProtectedRoute></ProtectedRoutesWrapper>} />
                  <Route path="/superadmin" element={<ProtectedRoutesWrapper><ProtectedRoute><SuperAdmin /></ProtectedRoute></ProtectedRoutesWrapper>} />
                  </Routes>
                </Suspense>
              </RouteLoadingIndicator>
            </BrowserRouter>
          </TooltipProvider>
        </FactoryLoginProvider>
      </OutletLoginProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
